import { RoleOnboardingConfig } from '../../types/onboarding';
import {
  DEMO_APP_ICON,
  DEMO_APP_DESCRIPTION,
  demoVersions,
  createDemoDataForUser,
  getDemoAppNameForRole
} from './demoData';

/**
 * Конфигурация онбординга для Developer
 * Той самий ShopApp Demo проєкт, але з фокусом на зміну статусів подій
 */
export const developerOnboardingConfig: RoleOnboardingConfig = {
  role: 'developer',
  demoAppName: getDemoAppNameForRole('developer'),
  demoAppIcon: DEMO_APP_ICON,
  demoAppDescription: DEMO_APP_DESCRIPTION,

  steps: [
    // === БЛОК 1: Вступ ===
    {
      id: 'dev-welcome',
      target: '[data-tour="user-profile"]',
      title: '👋 Вітаємо, {displayName}!',
      description: 'Цей інтерактивний тур познайомить вас з роботою подій аналітики. Як розробник ви можете переглядати всі події, змінювати їх статуси та додавати коментарі. Ви дізнаєтесь, як переглядати події, змінювати їх статуси та відстежувати впровадження. Ви НЕ можете створювати нові події або редагувати їх параметри - це робота аналітика.',
      position: 'bottom'
    },

    // === БЛОК 2: Додатки ===
    {
      id: 'dev-apps-intro',
      target: '[data-tour="app-switcher"]',
      title: '📱 Розділ 1: Додатки',
      description: 'Ви маєте доступ до додатків, до яких вас запросили як розробника. Тут відображаються всі доступні додатки. Ви можете переглядати їх, але не створювати нові. Кожен додаток містить версії та макети з подіями.',
      position: 'right'
    },

    // === БЛОК 3: Версії ===
    {
      id: 'dev-version-selector',
      target: '[data-tour="version-selector"]',
      title: '📋 Список версій',
      description: 'Тут відображаються всі версії додатку. Активна версія підсвічена. Версія 2.0 позначена як "Поточна" та містить нові події, які потребують вашого впровадження.',
      position: 'bottom'
    },
    {
      id: 'dev-compare-versions',
      target: '[data-tour="compare-versions"]',
      title: '🔍 Порівняння версій',
      description: 'Ця кнопка відкриває режим порівняння двох версій. Ви можете побачити, які події додалися, видалилися або змінилися між версіями 1.0 та 2.0.',
      position: 'bottom'
    },

    // === БЛОК 4: Макети ===
    {
      id: 'dev-screens-list',
      target: '[data-tour="screens-list"]',
      title: '📑 Список макетів',
      description: 'Тут відображаються всі макети поточної версії. Натисніть на макет, щоб відкрити його на канві та побачити події. Зверніть увагу на індикатори кількості подій.',
      position: 'right'
    },

    // === БЛОК 5: Канва та події ===
    {
      id: 'dev-canvas-area',
      target: '[data-tour="canvas"]',
      title: '🖱️ Робота з канвою',
      description: 'Використовуйте колесо миші для масштабування, перетягування для навігації. Подвійний клік скидає масштаб. Події відображаються як кольорові маркери на макеті.',
      position: 'center'
    },
    {
      id: 'dev-event-markers',
      target: '[data-tour="event-marker"]',
      title: '📌 Маркери подій',
      description: 'Кожна подія відображається як маркер з номером. Колір маркера відповідає статусу події. Натисніть на маркер, щоб відкрити деталі події в правій панелі.',
      position: 'right',
      action: () => {
        const store = (window as any).__STORE__;
        if (store) {
          const state = store.getState();
          const currentScreen = state.currentScreen;
          if (currentScreen?.events?.length > 0) {
            // Select first event to show details
            store.getState().selectEvent(currentScreen?.events?.[0]?.id);
          }
        }
      }
    },

    // === БЛОК 6: Статуси подій (ключовий для розробника) ===
    {
      id: 'dev-event-details',
      target: '[data-tour="event-details"]',
      title: '📝 Деталі події',
      description: 'Права панель показує деталі вибраної події: назву, опис, тип, статус, параметри та коментарі. Розробник може змінювати статус та додавати коментарі.',
      position: 'left'
    },
    {
      id: 'dev-event-statuses',
      target: '[data-tour="status-filter"]',
      title: '🏷️ Статуси подій',
      description: 'Події мають 5 статусів, які показують їхній стан впровадження: 🟢 Нова - щойно створена, очікує впровадження; 🟡 Змінено - аналітик вніс правки; 🔵 Впроваджено - код вже в продакшені; 🔴 На видалення - застаріла подія; ⚪ Видалено - повністю видалена. Ви можете змінювати статуси відповідно до прогресу розробки.',
      position: 'right',
      action: () => {
        // Open status dropdown
        window.dispatchEvent(new CustomEvent('tour-open-status-dropdown'));
      }
    },

    // === БЛОК 8: Робота з подіями ===
    {
      id: 'dev-event-params',
      target: '[data-tour="event-params"]',
      title: '🔧 Параметри події',
      description: 'Кожна подія має параметри - дані, які передаються з подією. Перевірте, що ви передаєте всі необхідні параметри з правильними типами (string, number, boolean).',
      position: 'left',
      action: () => {
        // Close status dropdown when moving to this step
        window.dispatchEvent(new CustomEvent('tour-close-status-dropdown'));
      }
    },
    {
      id: 'dev-event-comments-info',
      target: '[data-tour="event-comments"]',
      title: '💬 Коментарі до подій',
      description: 'Додавайте коментарі до подій, щоб спілкуватися з аналітиками. Задавайте питання, повідомляйте про проблеми впровадження або уточнюйте вимоги. Всі коментарі зберігаються в історії події.',
      position: 'left'
    },

    // === БЛОК 9: Фільтри та пошук ===
    {
      id: 'dev-filters',
      target: '[data-tour="filters"]',
      title: '⚡ Фільтри подій',
      description: 'Фільтруйте події за статусом (Нова, На видалення) та типом. Це допоможе зосередитися на подіях, які потребують впровадження або видалення.',
      position: 'left'
    },
    {
      id: 'dev-search',
      target: '[data-tour="search"]',
      title: '🔍 Пошук подій',
      description: 'Використовуйте пошук для знаходження конкретних подій за назвою. Корисно при роботі з великою кількістю подій на макеті.',
      position: 'left'
    },
    {
      id: 'dev-history',
      target: '[data-tour="history-panel"]',
      title: '📜 Історія змін',
      description: 'Відстежуйте всю історію змін подій: хто і коли створив подію, змінив статус, додав коментар. Це допомагає зрозуміти контекст та причини змін.',
      position: 'right',
      action: () => {
        // Open history panel
        window.dispatchEvent(new CustomEvent('tour-open-history-panel'));
      }
    },

    // === БЛОК 10: Командна робота та робочий процес ===
    {
      id: 'dev-team-and-workflow',
      target: '',
      title: '👥 Робота в команді',
      description: 'Ви працюєте в команді з аналітиками: аналітик створює події та визначає вимоги, розробник впроваджує події в код та змінює статуси. Робочий процес: 1️⃣ Перегляньте нові події (🟢), 2️⃣ Впровадьте код, 3️⃣ Змініть статус на "Впроваджено" (🔵), 4️⃣ Перевірте події "На видалення" (🔴), 5️⃣ Позначте як "Видалено" (⚪).',
      position: 'center',
      action: () => {
        // Close history panel when moving to this step
        window.dispatchEvent(new CustomEvent('tour-close-history-panel'));
      }
    },

    // === БЛОК 11: Експорт даних ===
    {
      id: 'dev-export',
      target: '[data-tour="export-panel"]',
      title: '📤 Експорт даних',
      description: 'Експортуйте всі події проєкту в форматі JSON або CSV для імпорту в Firebase Analytics або інші системи. Корисно для резервного копіювання або інтеграції з іншими інструментами.',
      position: 'bottom'
    },

    // === БЛОК 12: Завершення ===
    {
      id: 'dev-completion',
      target: '',
      title: '🎉 Вітаємо! Тур завершено',
      description: 'Ви ознайомились з можливостями розробника! Тепер ви можете переглядати події, змінювати їх статуси та спілкуватися з аналітиками через коментарі. Спробуйте попрактикуватись на цьому демо-проєкті!',
      position: 'center'
    }
  ],

  demoData: {
    name: getDemoAppNameForRole('developer'),
    description: DEMO_APP_DESCRIPTION,
    versions: demoVersions
  }
};

/**
 * Отримує демо-дані для Developer з унікальними ID
 * Версії відсортовані: активна версія (2.0) йде першою
 * Использует реальные данные из экспорта JSON
 */
export function getDeveloperDemoData(userId: string): RoleOnboardingConfig['demoData'] {
  return createDemoDataForUser(userId, 'developer');
}
